<template>
  <Section
    :id="id"
    :aria-label="t('events.events')"
    :loading="serviceIncidentTypes.loading || isSaving || commentsLoading"
    class="shipment-event-section"
  >
    <template #header="{ headerId, loading }">
      <ShipmentEventSectionHeader
        :id="headerId"
        v-model:active-tab-key="activeTabKey"
        v-model:showing-timeline="isShowingTimeline"
        :tabs="tabs"
        :tab-state-key="tabStateKey"
        :shipment-events="shipmentEvents"
        :shipment-service-incidents="shipmentServiceIncidents"
        :tender-requests="tenderRequests"
        :loading="loading"
      />
    </template>

    <template #default>
      <Tabs v-model:value="activeTabKey" class="shipment-event-section-tabs">
        <TabPanels>
          <TabPanel value="EVENTS">
            <ShipmentTimelineContainer
              v-if="authorization.hasAuthority('shipmentEvent', 'read')"
              ref="shipmentTimelineContainer"
              v-model:showing-timeline="isShowingTimeline"
              v-model:saving="isSaving"
              :shipment="shipment"
              :shipment-events="shipmentEvents"
              :shipment-service-incidents="shipmentServiceIncidents"
              :shipment-stops="shipment.stops"
              :tender-requests="tenderRequests"
              :service-incident-types="serviceIncidentTypes"
              :editable="isEditable"
              :show-entries-without-locations="showEntriesWithoutLocations"
              @shipment-event-or-service-incident-saved="
                emit('shipment-event-or-service-incident-saved', $event)
              "
            />
          </TabPanel>

          <TabPanel value="COMMENTS">
            <CommentList
              :comments-with-replies="commentsWithReplies.data"
              :loading="commentsWithReplies.loading"
              :loading-more="commentsLoadingMore"
              :load-more-comments="loadMoreComments"
              :current-user="currentUser"
              :user-store="userStore"
              :comment-loading="commentLoading"
              :mentionable-users="mentionableUsers.data"
              :readonly="!hasShipmentUpdatePermission"
              @create="onCommentCreate"
              @update="onCommentUpdate"
              @reply="onCommentReply"
              @delete="
                requestDeleteConfirmation({
                  param: t('comments.comments'),
                  accept: () => onCommentDelete($event),
                })
              "
              @load-more="onCommentLoadMore"
              @load-more-reply="onCommentReplyLoadMore"
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </template>

    <template v-if="activeTabKey === 'EVENTS'" #footer>
      <div class="shipment-event-section-footer-left">
        <AddButton
          v-if="hasShipmentEventCreatePermission && isEditable"
          mode="tertiary"
          :icon="PrimeIcons.PLUS"
          :label="t('common.newParam', { param: t('events.events') })"
          :loading="isSaving"
          @click="shipmentTimelineContainer?.onAddShipmentEvent"
        />
        <AddButton
          v-if="
            hasShipmentEventCreatePermission &&
            isEditable &&
            !!serviceIncidentTypes.data?.length
          "
          mode="tertiary"
          :icon="PrimeIcons.PLUS"
          :label="t('common.newParam', { param: t('events.incidents') })"
          :loading="isSaving"
          @click="shipmentTimelineContainer?.onAddServiceIncident"
        />
      </div>

      <div class="shipment-event-section-footer-right">
        <WrappedSwitch
          id="show-entries-without-locations"
          v-model="showEntriesWithoutLocations"
          :label="t('events.showEventsWithoutLocations')"
          inline
        />
      </div>
    </template>
  </Section>
</template>

<script lang="ts" setup>
import { PrimeIcons } from '@primevue/core/api';
import { useLocalStorage } from '@vueuse/core';
import type { PropType, Ref } from 'vue';
import { computed, ref, toRef, toRefs, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import {
  useDeleteConfirmation,
  usePartitionedStorageKey,
  useRygenTabs,
} from '@rygen/rygen-ui/composables';
import {
  ActionCommand,
  Loadable,
  Module,
  ServiceIncidentType,
  Tab,
  UserRef,
} from '@rygen/rygen-ui/model';

import { useShipmentComments } from '@/composables';
import {
  Shipment,
  ShipmentAction,
  ShipmentEvent,
  ShipmentServiceIncident,
  ShipmentStatus,
  TenderRequest,
} from '@/model';
import { useAssignableUserStore } from '@/stores/AssignableUser';
import { useCurrentUserStore } from '@/stores/CurrentUser';
import { useUserStore } from '@/stores/User';
import { isStatusEqualOrAfter } from '@/util';

import ShipmentEventSectionHeader, {
  type TabKey,
} from '@/components/shipment/ShipmentEventSectionHeader.vue';
import ShipmentTimelineContainer from '@/components/shipment/ShipmentTimelineContainer.vue';
import AddButton from '@/components/ui/AddButton.vue';
import CommentList from '@rygen/rygen-ui/components/comment/CommentList.vue';
import WrappedSwitch from '@rygen/rygen-ui/components/form/WrappedSwitch.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';
import TabPanel from 'primevue/tabpanel';
import TabPanels from 'primevue/tabpanels';
import Tabs from 'primevue/tabs';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  shipment: {
    type: Shipment,
    default: () => new Shipment(),
  },
  shipmentEvents: {
    type: Array as PropType<ShipmentEvent[]>,
    default: () => [],
  },
  shipmentServiceIncidents: {
    type: Array as PropType<ShipmentServiceIncident[]>,
    default: () => [],
  },
  tenderRequests: {
    type: Array as PropType<TenderRequest[]>,
    default: () => [],
  },
  serviceIncidentTypes: {
    type: Object as PropType<Loadable<ServiceIncidentType[]>>,
    default: () => ({ loading: false }),
  },
});
const {
  id,
  shipment,
  shipmentEvents,
  shipmentServiceIncidents,
  tenderRequests,
  serviceIncidentTypes,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'command', payload: ActionCommand<ShipmentAction>): void;
  (
    event: 'shipment-event-or-service-incident-saved',
    newVal: ShipmentEvent | ShipmentServiceIncident,
  ): void;
}>();

const shipmentTimelineContainer = useTemplateRef('shipmentTimelineContainer');

const shipmentId = toRef(() => shipment.value.id ?? -1);

const { loadAssignableUsersByDomainId } = useAssignableUserStore();
const { currentUser, selectedDomain } = useCurrentUserStore();
const { requestDeleteConfirmation } = useDeleteConfirmation();
const { t } = useI18n();
const {
  commentsWithReplies,
  loadMoreComments,
  commentLoading,
  commentsLoading,
  commentsLoadingMore,
  totalCommentsCount,
  loadComments,
  loadCommentsCount,
  onCommentCreate,
  onCommentUpdate,
  onCommentReply,
  onCommentDelete,
  onCommentLoadMore,
  onCommentReplyLoadMore,
} = useShipmentComments(shipmentId);
const userStore = useUserStore();

const { storageKey: showEntriesWithoutLocationsKey } = usePartitionedStorageKey(
  'show-timeline-entries-without-locations',
  {
    module: Module.TMS,
    currentUser,
    selectedDomain,
  },
);
const showEntriesWithoutLocations = useLocalStorage(
  showEntriesWithoutLocationsKey.value,
  false,
);

const tabs = computed((): Tab<TabKey>[] => [
  {
    key: 'EVENTS',
    header: t('events.eventsAndMilestones'),
    visible: authorization.hasAuthority('shipmentEvent', 'read'),
  },
  {
    key: 'COMMENTS',
    header: t('comments.comments', 2),
    count: totalCommentsCount.value,
    visible: true,
  },
]);
const { activeTabKey, tabStateKey } = useRygenTabs<TabKey, Tab<TabKey>>(tabs);

const isShowingTimeline = ref(false);
const isSaving = ref(false);
const mentionableUsers: Ref<Loadable<UserRef[]>> = ref({ loading: false });

const hasShipmentUpdatePermission = computed(() => {
  return authorization.hasAuthority('shipment', 'update');
});

const isShipmentEditingLocked = computed(() => {
  return isStatusEqualOrAfter(shipment.value.status, ShipmentStatus.COMPLETE);
});

const isEditable = computed(() => {
  return hasShipmentUpdatePermission.value && !isShipmentEditingLocked.value;
});

const hasShipmentEventCreatePermission = computed(() => {
  return authorization.hasAuthority('shipmentEvent', 'create');
});

const loadAssignableUsers = async () => {
  const ownerId = shipment.value.owner?.id;
  if (!ownerId) {
    console.error('Cannot determine owner id for shipment', shipment.value);
    return;
  }

  mentionableUsers.value = loadAssignableUsersByDomainId(ownerId, 'shipment');
};

watch(
  shipmentId,
  async (_newVal) => {
    loadAssignableUsers();
    loadComments();
    loadCommentsCount();
  },
  { immediate: true },
);

defineExpose({
  showEventModal(event: ShipmentEvent) {
    shipmentTimelineContainer.value?.showEventModal(event);
  },
  showIncidentModal(incident: ShipmentServiceIncident) {
    shipmentTimelineContainer.value?.showIncidentModal(incident);
  },
  toggleTimeline(newVal?: boolean) {
    shipmentTimelineContainer.value?.toggleTimeline(newVal);
  },
});
</script>

<style>
.shipment-event-section {
  --p-tabs-tabpanel-padding: 0;

  .section-header {
    --section-header-margin: 0 0 var(--sp-6);
  }

  .section-data {
    display: flex;
    flex-direction: column;
    gap: var(--gap-1);

    .shipment-event-section-tabs {
      .p-tabpanel-active {
        .comment-list {
          .comment-list-comments:empty {
            display: none;
          }
        }
      }
    }
  }

  .section-footer {
    display: flex;
    justify-content: space-between;
    gap: var(--gap-1);
    margin-top: var(--sp-3);

    .shipment-event-section-footer-left {
      display: flex;
      gap: var(--gap-1);
      align-items: start;
    }

    .shipment-event-section-footer-right {
      display: flex;
      align-items: flex-end;
    }
  }
}
</style>
